import React from "react"
import { IconChevronDown, IconCheck } from "@tabler/icons-react"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@love-irish/ui-components/dropdown-menu"

export interface OptionSelectOption {
  value: string
  label: string
  /** A short "what is this?" line shown under the label — the teaching bit. */
  description?: string
  /** A secondary term beside the title, e.g. the Irish name of a part of speech. */
  hint?: string
  /** A rendered icon node (e.g. <PartOfSpeechIcon type="verb" />). */
  icon?: React.ReactNode
}

/** A labelled group of options — its header (and optional blurb) explains the category to a learner. */
export interface OptionSelectGroup {
  label: string
  description?: string
  options: OptionSelectOption[]
}

/**
 * A select that teaches. Instead of bare <option>s, each choice shows an icon, a title, and a short
 * description — so a learner choosing "verb" or a conjugation class sees what it means. Reusable
 * anywhere a form choice benefits from explanation (parts of speech, verb classes, …). Define the
 * options with their descriptions once (PART_OF_SPEECH_META, POS_GRAMMAR) and pass them here.
 */
const Option: React.FC<{
  option: OptionSelectOption
  selected: boolean
  onChange: (value: string) => void
}> = ({ option, selected, onChange }) => (
  <DropdownMenuItem onClick={() => onChange(option.value)} className="flex items-start gap-2">
    {option.icon && <span className="mt-0.5 shrink-0">{option.icon}</span>}
    <span className="min-w-0">
      <span className="flex items-center gap-1 font-medium">
        {option.label}
        {option.hint && <span className="font-normal text-muted-foreground">/ {option.hint}</span>}
        {selected && <IconCheck size={13} className="text-primary-600" aria-label="selected" />}
      </span>
      {option.description && (
        <span className="block text-xs font-normal text-muted-foreground">{option.description}</span>
      )}
    </span>
  </DropdownMenuItem>
)

export const OptionSelect: React.FC<{
  value: string
  onChange: (value: string) => void
  /** Flat options, or use `groups` to label categories. Exactly one of the two. */
  options?: OptionSelectOption[]
  /** Grouped options: each group's header (and blurb) teaches what the category means. */
  groups?: OptionSelectGroup[]
  placeholder?: string
  disabled?: boolean
  ariaLabel?: string
  id?: string
}> = ({ value, onChange, options, groups, placeholder = "Choose…", disabled, ariaLabel, id }) => {
  const flat = groups ? groups.flatMap((g) => g.options) : options ?? []
  const selected = flat.find((o) => o.value === value)
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        id={id}
        aria-label={ariaLabel}
        disabled={disabled}
        className="mt-1 flex w-full items-center justify-between gap-2 rounded border border-secondary-300 p-2 text-left disabled:opacity-50"
      >
        <span className="flex min-w-0 items-center gap-2">
          {selected?.icon}
          <span className="truncate">{selected ? selected.label : placeholder}</span>
          {selected?.hint && (
            <span className="truncate font-normal text-muted-foreground">/ {selected.hint}</span>
          )}
        </span>
        <IconChevronDown size={16} className="shrink-0 text-muted-foreground" aria-hidden />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-[50vh] w-72 overflow-y-auto">
        {groups
          ? groups.map((group, i) => (
              <React.Fragment key={group.label}>
                {i > 0 && <DropdownMenuSeparator />}
                <DropdownMenuLabel className="pb-0">{group.label}</DropdownMenuLabel>
                {group.description && (
                  <p className="px-2 pb-1 text-xs font-normal text-muted-foreground">
                    {group.description}
                  </p>
                )}
                {group.options.map((option) => (
                  <Option
                    key={option.value}
                    option={option}
                    selected={option.value === value}
                    onChange={onChange}
                  />
                ))}
              </React.Fragment>
            ))
          : (options ?? []).map((option) => (
              <Option
                key={option.value}
                option={option}
                selected={option.value === value}
                onChange={onChange}
              />
            ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default OptionSelect
